/**
 * AccountSetupPanel — the hub's zero-state.
 *
 * When no site on the account has ever reported data, the dashboard has nothing
 * to show: every metric is 0 and every table cell is an em dash. Rendering the
 * full command center in that state produced four competing orange CTAs (Add
 * Site / Install tracker / Review your first test / Setup MCP) around a screen
 * with no content, and buried the one action that unblocks the product.
 *
 * So the hub collapses to this: one job, one primary button. Everything else
 * (MCP, sites table, plan card, metric strip) returns on the first pageview.
 *
 * The pending first-test recommendation appears here as a *promise*, not a
 * second CTA. You cannot run a test on a page the script is not live on, so
 * offering that button would contradict the instruction directly above it.
 */
import { useNavigate } from "react-router-dom"
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowRight01Icon } from "@hugeicons/core-free-icons"

import { Button } from "@/components/ui/button"
import { track } from "@/lib/utils/track"
import { useAuth } from "@/lib/contexts/AuthContext"

export interface SetupSite {
  propertyId: string
  domain: string
}

interface AccountSetupPanelProps {
  sites: SetupSite[]
}

export default function AccountSetupPanel({ sites }: AccountSetupPanelProps) {
  const navigate = useNavigate()
  const { token } = useAuth()

  if (sites.length === 0) return null
  const primary = sites[0]
  const extra = sites.length - 1

  const goInstall = () => {
    track("setup_panel_install_clicked", token, {
      property_id: primary.propertyId,
      site_count: sites.length,
    })
    navigate(
      `/onboarding/install-script?propertyId=${encodeURIComponent(primary.propertyId)}&domain=${encodeURIComponent(primary.domain)}`
    )
  }

  // Constrained to its content rather than spanning the grid: a full-bleed card
  // with a 576px column left ~1000px of empty surface and read as a container
  // that failed to fill.
  return (
    <div className="well max-w-2xl">
      <div className="surface-raised px-6 py-7 sm:px-8 sm:py-8">
        <p className="text-xs font-medium leading-4 text-muted-foreground">
          Finish setup
        </p>

        <h2 className="mt-2 text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">
          Connect <span className="tabular-nums">{primary.domain}</span>
        </h2>

        <p className="mt-2 text-[13px] leading-5 text-muted-foreground">
          One script tag in your page head. Analytics, heatmaps, funnels, and A/B
          tests all start reporting from the same install.
          {extra > 0 && ` ${extra} other ${extra === 1 ? "site is" : "sites are"} waiting too.`}
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-4">
          <Button onClick={goInstall} className="h-[34px] gap-1.5 rounded-lg px-3 text-xs font-medium">
            Install tracker
            <HugeiconsIcon icon={ArrowRight01Icon} size={13} />
          </Button>
          <button
            type="button"
            onClick={goInstall}
            className="text-[13px] text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
          >
            Send it to a developer
          </button>
        </div>

        {/* Status, not an action: a live dot with ink text (White signal v2). */}
        <div className="mt-6 flex items-center gap-2 border-t border-border/60 pt-4">
          <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-live motion-reduce:animate-none" />
          <p className="text-xs leading-4 text-foreground">
            Listening for the first pageview. This page updates on its own.
          </p>
        </div>

      </div>
    </div>
  )
}
